import { mailLetterApi } from '@/apis/core';

import { useMutationWrapper } from '@/apis/common/useWrapper';

import {
  GetMailLetterSetting200ResponseBody,
  GetMailLetterProperty200ResponseBody,
  PostMailLetterPropertyRequest,
  UpdateMailLetterSettingRequest,
  OpeMailLetterPropertyRequest,
  OpeMailLetterPropertyResponseBody,
  OpeMailLetterRequest,
  OpeMailLetterSettingResponseBody,
  OpeMailLetterAgreeRequest,
  UpdateOpeMailLetterAgree200Response
} from 'openapi/omise-craft-core-spec';
import { useQuery } from '@tanstack/react-query';

export interface mailList {
  column_flg: boolean;
  seminar_flg: boolean;
  campaign_flg: boolean;
  property_flg: boolean;
  property_init_flg?: boolean;
  property_line_messaging_flg: boolean;
}

export interface setteingPropertyType {
  stationId: number | undefined;
  areaId: number | undefined;
}

export interface targetPropertyType {
  Id: number | undefined;
  StationId: number | undefined;
  StationName: string | undefined;
  AreaId: number | undefined;
  PrefectureName: string | undefined;
  AreaName: string | undefined;
}

// メール配信同意設定の取得
export const getMailLetterSetting = async (uid?: string) => {
  const res = await mailLetterApi.getMailLetterSetting(uid);
  return res.data.body;
};
export const useGetMailLetter = (uid?: string) => {
  return useQuery<GetMailLetterSetting200ResponseBody, Error>(
    ['get_mail_letter_setting', uid],
    () => getMailLetterSetting(uid),
    {
      refetchOnWindowFocus: false,
      retry: false
    }
  );
};

// メール配信同意設定の更新
export const updateMailLetterSetting = async (
  uid?: string,
  updateMailLetterSettingRequest?: UpdateMailLetterSettingRequest
) => {
  const res = await mailLetterApi.updateMailLetterSetting(uid, updateMailLetterSettingRequest);
  return res.data.body;
};
export const useUpdateMailLetterSetting = (uid?: string) => {
  return useMutationWrapper({
    mutationKey: ['update_mail_letter_setting'],
    mutationFn: () => updateMailLetterSetting(uid)
  });
};

// メール配信（新着物件）設定情報取得
export const getMailLetterProperty = async (uid?: string) => {
  const res = await mailLetterApi.getMailLetterProperty(uid);
  return res.data.body;
};
export const useGetMailLetterProperty = (uid?: string) => {
  return useQuery<GetMailLetterProperty200ResponseBody, Error>(
    ['get_mail_letter_property', uid],
    () => getMailLetterProperty(uid),
    {
      refetchOnWindowFocus: false,
      retry: false
    }
  );
};

// メール配信（新着物件）設定情報登録
export const postMailLetterProperty = async (
  uid?: string,
  postMailLetterPropertyRequest?: PostMailLetterPropertyRequest
) => {
  const res = await mailLetterApi.postMailLetterProperty(uid, postMailLetterPropertyRequest);
  return res.data.body;
};
export const usePostMailLetterProperty = (uid?: string) => {
  return useMutationWrapper({
    mutationKey: ['post_mail_letter_property'],
    mutationFn: (param: PostMailLetterPropertyRequest) => {
      return postMailLetterProperty(uid, param);
    }
  });
};

// メール配信（新着物件）設定情報解除
export const updateMailLetterProperty = async (uid?: string) => {
  const res = await mailLetterApi.updateMailLetterProperty(uid);
  return res.data.body;
};
export const useUpdateMailLetterProperty = (uid?: string) => {
  return useMutationWrapper({
    mutationKey: ['update_mail_letter_property'],
    mutationFn: () => updateMailLetterProperty(uid)
  });
};

// （運用者）メール配信同意設定の取得
export const getOpeMailLetterSetting = async (memberId: number) => {
  const res = await mailLetterApi.getOpeMailLetterSetting(memberId);
  return res.data.body;
};

export const useGetOpeMailLetterSetting = (memberId: number | undefined, enabled = true) => {
  return useQuery<OpeMailLetterSettingResponseBody, Error>(
    ['get_ope_mail_letter_setting', memberId],
    () => {
      if (memberId === undefined) {
        // enabled=falseで呼ばれる想定だが、型のためガード
        throw new Error('memberId is required');
      }
      return getOpeMailLetterSetting(memberId);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && memberId !== undefined
    }
  );
};

// （運用者）メール配信同意設定の更新
export const updateOpeMailLetterSetting = async (memberId: number, opeMailLetterRequest?: OpeMailLetterRequest) => {
  const res = await mailLetterApi.updateOpeMailLetterSetting(memberId, opeMailLetterRequest);
  return res.data.body;
};

export const useUpdateOpeMailLetterSetting = (memberId: number) => {
  return useMutationWrapper({
    mutationKey: ['update_ope_mail_letter_setting', memberId],
    mutationFn: (param: OpeMailLetterRequest) => updateOpeMailLetterSetting(memberId, param)
  });
};

// （運用者）メール配信（新着物件）設定情報取得
export const getOpeMailLetterProperty = async (memberId: number) => {
  const res = await mailLetterApi.getOpeMailLetterProperty(memberId);
  return res.data.body;
};

export const useGetOpeMailLetterProperty = (memberId: number | undefined, enabled = true) => {
  return useQuery<OpeMailLetterPropertyResponseBody, Error>(
    ['get_ope_mail_letter_property', memberId],
    () => {
      if (memberId === undefined) {
        throw new Error('memberId is required');
      }
      return getOpeMailLetterProperty(memberId);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && memberId !== undefined
    }
  );
};

// （運用者）メール配信（新着物件）設定情報登録
export const postOpeMailLetterProperty = async (
  memberId: number,
  opeMailLetterPropertyRequest?: OpeMailLetterPropertyRequest
) => {
  const res = await mailLetterApi.postOpeMailLetterProperty(memberId, opeMailLetterPropertyRequest);
  return res.data.body;
};

export const usePostOpeMailLetterProperty = (memberId: number) => {
  return useMutationWrapper({
    mutationKey: ['post_ope_mail_letter_property', memberId],
    mutationFn: (param: OpeMailLetterPropertyRequest) => postOpeMailLetterProperty(memberId, param)
  });
};

// （運用者）メール配信（新着物件）設定情報解除
export const updateOpeMailLetterProperty = async (memberId: number) => {
  const res = await mailLetterApi.updateOpeMailLetterProperty(memberId);
  return res.data.body;
};

export const useUpdateOpeMailLetterProperty = (memberId: number) => {
  return useMutationWrapper({
    mutationKey: ['update_ope_mail_letter_property', memberId],
    mutationFn: () => updateOpeMailLetterProperty(memberId)
  });
};

// （運用者）お知らせメール（メール配信同意）の更新
export const updateOpeMailLetterAgree = async (memberId: number, request: OpeMailLetterAgreeRequest) => {
  const res = await mailLetterApi.updateOpeMailLetterAgree(request, memberId);
  return res.data;
};

export const useUpdateOpeMailLetterAgree = (memberId: number) => {
  return useMutationWrapper({
    mutationKey: ['update_ope_mail_letter_agree', memberId],
    mutationFn: (param: OpeMailLetterAgreeRequest): Promise<UpdateOpeMailLetterAgree200Response> =>
      updateOpeMailLetterAgree(memberId, param)
  });
};
